// SPDX-License-Identifier: AGPL-3.0-or-later

import type {OkeyStage} from '@app/features/table_game/components/okey/Okey101Staging';
import {stagedTiles} from '@app/features/table_game/components/okey/Okey101Staging';
import {type OkeyFace, okeyFaceOf, okeyTileLook} from '@app/features/table_game/components/okey/OkeyRackModel';
import type {Okey101Meld, Okey101View} from '@fluxer/schema/src/domains/table_game/TableGameSchemas';

export type MeldEnd = 'start' | 'end';
type Addition = OkeyStage['add'][number];
type Look = NonNullable<ReturnType<typeof okeyTileLook>>;

const isWild = (look: Look) => look.okey === 'wild';

// The server's group rule: 3 or 4 tiles, one number, no colour twice; wild okeys stand in for anything.
function isGroup(looks: ReadonlyArray<Look>): boolean {
	if (looks.length < 3 || looks.length > 4) return false;
	let number = 0;
	const colors = new Set<number>();
	for (const look of looks) {
		if (isWild(look)) continue;
		if ((number !== 0 && look.face.number !== number) || colors.has(look.face.color)) return false;
		number = look.face.number;
		colors.add(look.face.color);
	}
	return number !== 0;
}

// The server's run rule: one colour, ascending by one, read in order; position 14 is the 1 after 13 (12-13-1).
function isRun(looks: ReadonlyArray<Look>): boolean {
	if (looks.length < 3 || looks.length > 13) return false;
	const anchorIndex = looks.findIndex((look) => !isWild(look));
	if (anchorIndex === -1) return false;
	const anchor = looks[anchorIndex]!.face;
	const starts = anchor.number === 1 ? [1 - anchorIndex, 14 - anchorIndex] : [anchor.number - anchorIndex];
	return starts.some(
		(start) =>
			start >= 1 &&
			start + looks.length - 1 <= 14 &&
			looks.every((look, index) => {
				if (isWild(look)) return true;
				const position = start + index;
				return look.face.color === anchor.color && look.face.number === (position === 14 ? 1 : position);
			}),
	);
}

// Mirrors the server's canExtend: the set read with the tiles joined at that end must still be its kind.
function canExtend(
	meld: Pick<Okey101Meld, 'kind' | 'tiles'>,
	tiles: ReadonlyArray<string>,
	at: MeldEnd,
	okey: OkeyFace | null,
) {
	if (tiles.length === 0 || meld.kind === 'pair') return false;
	const joined = at === 'start' ? [...tiles, ...meld.tiles] : [...meld.tiles, ...tiles];
	const looks = joined.map((token) => okeyTileLook(token, okey));
	if (looks.some((look) => look === null)) return false;
	return meld.kind === 'run' ? isRun(looks as Array<Look>) : isGroup(looks as Array<Look>);
}

export function fittingEnds(
	meld: Pick<Okey101Meld, 'kind' | 'tiles'>,
	tile: string,
	okey: OkeyFace | null,
): Array<MeldEnd> {
	return (['start', 'end'] as const).filter((at) => canExtend(meld, [tile], at, okey));
}

// The set as it will be once the staged additions are applied in order (the server applies them the same way).
function withStaged(meld: Okey101Meld, add: ReadonlyArray<Addition>): Okey101Meld {
	let tiles = meld.tiles;
	for (const addition of add) {
		if (addition.meld_id !== meld.id) continue;
		tiles = addition.at === 'start' ? [...addition.tiles, ...tiles] : [...tiles, ...addition.tiles];
	}
	return {...meld, tiles};
}

// Opened, and the server has not said adding waits (the opening turn); an older server sends no can_add.
export function mayAdd(game: Okey101View, me: number): boolean {
	return game.opened[me - 1] !== undefined && game.opened[me - 1] !== 'none' && game.can_add !== false;
}

// One tile onto one set: at the requested end if it fits there, else the end it fits (the end when both do).
export function meldAddition(
	game: Okey101View,
	stage: OkeyStage | null,
	tile: string,
	meldId: number,
	at?: MeldEnd,
): Addition | null {
	const meld = game.melds.find((candidate) => candidate.id === meldId);
	if (!meld) return null;
	const ends = fittingEnds(withStaged(meld, stage?.add ?? []), tile, okeyFaceOf(game.indicator));
	const end = at ? (ends.includes(at) ? at : null) : ends.includes('end') ? 'end' : (ends[0] ?? null);
	return end ? {meld_id: meldId, tiles: [tile], at: end} : null;
}

// Several tiles onto one set, each at an end it fits, in whatever order they fit; null when one never does.
export function meldAdditions(
	game: Okey101View,
	stage: OkeyStage | null,
	tiles: ReadonlyArray<string>,
	meldId: number,
): Array<Addition> | null {
	let next: OkeyStage = stage ?? {take: null, open: [], add: []};
	const additions: Array<Addition> = [];
	const left = [...tiles];
	while (left.length > 0) {
		const index = left.findIndex((tile) => meldAddition(game, next, tile, meldId) !== null);
		if (index === -1) return null;
		const addition = meldAddition(game, next, left.splice(index, 1)[0]!, meldId)!;
		additions.push(addition);
		next = {...next, add: [...next.add, addition]};
	}
	return additions.length > 0 ? additions : null;
}

// "Otomatik işle": every rack tile that fits a set on the table, staged on top of the current stage, repeated while
// one addition makes room for another. Never a wild okey (better kept), and one tile always stays to discard.
// Null when it adds nothing or adding is not mine to do now.
export function autoAddStage(game: Okey101View, me: number, stage: OkeyStage | null): OkeyStage | null {
	const rack = game.rack;
	if (!rack || game.turn !== me || game.phase !== 'play' || !mayAdd(game, me) || stage?.take) {
		return null;
	}
	const okey = okeyFaceOf(game.indicator);
	const base: OkeyStage = stage ?? {take: null, open: [], add: []};
	const next: OkeyStage = {...base, add: [...base.add]};
	const used = new Set(stagedTiles(stage));
	let progress = true;
	while (progress) {
		progress = false;
		for (const tile of rack) {
			if (used.has(tile) || rack.length - used.size <= 1 || okeyTileLook(tile, okey)?.okey === 'wild') continue;
			const addition = game.melds.map((meld) => meldAddition(game, next, tile, meld.id)).find(Boolean);
			if (!addition) continue;
			next.add.push(addition);
			used.add(tile);
			progress = true;
		}
	}
	return next.add.length > base.add.length ? next : null;
}
